import React, { useEffect, useRef, useState } from "react";
import { createRoot } from "react-dom/client";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  ChevronRight,
  Copy,
  Folder,
  Map as MapIcon,
  Plus,
  Search,
  LogOut,
  Save,
  Eye,
  Undo2,
  Redo2,
  MousePointer2,
  MapPin,
  Flag,
  MoveRight,
  Route,
  TriangleAlert,
  Type,
  Square,
  PenLine,
  Lock,
  Send,
  X,
  Menu,
  Download,
  Settings,
  Users,
  History,
  MessageSquare,
  Share2,
  Trash2,
} from "lucide-react";
import QRCode from "qrcode";
import {
  featureKinds,
  featureSchema,
  mapDocumentSchema,
  publishDocument,
  mergeDrafts,
  recoverDraft,
  templates,
  type MapDocument,
  type MapFeature,
  type Coordinate,
  type Conflict,
  type Locale,
} from "@51time/domain";
import { api, ApiError, date, uploadPhoto } from "./api";
import {
  getDraft,
  putDraft,
  clearDraft,
  queueFeedback,
  feedbackQueue,
  clearFeedback,
} from "./local";
import { MapCanvas } from "./MapCanvas";
import { PublicExamples } from "./PublicExamples";
import { viewerText } from "./viewer-text";
import { exportBackup, importBackup } from "./backup";
import { downloadQRSign } from "./qr-sign";
import { joinProject } from "./join-project";
import "./style.css";

type User = { id: string; name: string; email: string };
type Project = {
  id: string;
  name: string;
  description: string;
  role: "admin" | "worker";
};
function Button({
  children,
  onClick,
  primary = false,
  disabled = false,
  title,
}: React.PropsWithChildren<{
  onClick?: () => void;
  primary?: boolean;
  disabled?: boolean;
  title?: string;
}>) {
  return (
    <button
      className={primary ? "primary" : ""}
      onClick={onClick}
      disabled={disabled}
      title={title}
    >
      {children}
    </button>
  );
}
function Brand() {
  return (
    <span className="brand">
      <img className="brand-mark" src="/website/mark.svg" alt="" />
      <span className="brand-name">
        WayTogether<span className="brand-sub">MAPS</span>
      </span>
    </span>
  );
}
function Notice({
  children,
  error = false,
}: React.PropsWithChildren<{ error?: boolean }>) {
  return (
    <div
      role={error ? "alert" : "status"}
      className={`notice ${error ? "error" : ""}`}
    >
      {children}
    </div>
  );
}
function Dialog({
  title,
  children,
  close,
  wide = false,
}: React.PropsWithChildren<{
  title: string;
  close: () => void;
  wide?: boolean;
}>) {
  return (
    <div className="modal-backdrop">
      <section
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className={`modal ${wide ? "wide" : ""}`}
      >
        <header>
          <h2>{title}</h2>
          <button onClick={close} aria-label="Close">
            <X />
          </button>
        </header>
        {children}
      </section>
    </div>
  );
}
function PasswordReset() {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [show, setShow] = useState(false);
  const [token] = useState(() => location.hash.slice(1));
  async function submit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);
    const password = String(data.get("newPassword") ?? "");
    if (password !== data.get("confirmPassword")) {
      setError("The two passwords do not match. Please check them and try again.");
      return;
    }
    setBusy(true); setError("");
    try {
      await api("/auth/reset-password", "POST", { token, newPassword: password });
      form.reset(); setShow(false); setDone(true);
      history.replaceState(null, "", "/reset-password");
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  }
  return <main className="auth-page"><section className="auth-intro">
    <Brand /><h1>Back to your<br />field desk.</h1>
    <p>Your saved maps and projects stay as they are.</p>
  </section><section className="auth-card">
    <span className="eyebrow">YOUR ACCOUNT</span>
    <h2>{done ? "Password updated" : "Choose a new password"}</h2>
    {done ? <><p>Sign in again with your new password on the web and in the installed app.</p><a href="/">Return to sign in</a></> : !token ? <Notice error>This reset link is missing or has already been used. Request a new private reset link.</Notice> : <>
      <p>This private link works once. Resetting signs out existing sessions.</p>
      {error && <Notice error>{error}</Notice>}
      <form onSubmit={submit}>
        <label>New password<input name="newPassword" type={show ? "text" : "password"} required minLength={12} maxLength={128} autoComplete="new-password" disabled={busy} /></label>
        <label>Confirm new password<input name="confirmPassword" type={show ? "text" : "password"} required minLength={12} maxLength={128} autoComplete="new-password" disabled={busy} /></label>
        <button type="button" onClick={() => setShow(!show)} aria-pressed={show}>{show ? "Hide passwords" : "Show passwords"}</button>
        <small>Use at least 12 characters.</small>
        <button className="primary" disabled={busy}>{busy ? "Updating…" : "Set new password"}</button>
      </form></>}
  </section></main>;
}
function AuthScreen({
  onDone,
  currentUser,
}: {
  onDone: () => void;
  currentUser?: User;
}) {
  const setup = location.pathname === "/setup",
    join = location.pathname === "/join";
  const token = location.hash.slice(1);
  const [error, setError] = useState(""),
    [busy, setBusy] = useState(false),
    [invite, setInvite] = useState<any>(null),
    [existingAccount, setExistingAccount] = useState(!!currentUser);
  useEffect(() => {
    if (join && token)
      api(`/invitations/${token}`)
        .then(setInvite)
        .catch((e) => setError(e.message));
  }, []);
  async function submit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setBusy(true);
    setError("");
    const values = Object.fromEntries(new FormData(e.currentTarget));
    try {
      if (setup) await api("/bootstrap", "POST", { ...values, token });
      if (join) {
        await joinProject(
          api,
          token,
          {
            name: String(values.name ?? ""),
            email: String(values.email),
            password: String(values.password),
          },
          existingAccount,
        );
      } else {
        await api("/auth/sign-in/email", "POST", {
          email: values.email,
          password: values.password,
        });
      }
      history.replaceState(null, "", "/");
      onDone();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <main className="auth-page">
      <section className="auth-intro">
        <Brand />
        <h1>
          One map.
          <br />
          Everyone on
          <br />
          the same page.
        </h1>
        <p>Mark what matters. Share the latest view.</p>
        <div className="intro-paper">
          <span>GATE A</span>
          <div className="sketch-road" />
          <span className="sketch-arrow">↗</span>
          <span className="sketch-stop">✕</span>
          <small>Simple marks. Clear instructions.</small>
        </div>
      </section>
      <section className="auth-card">
        <span className="eyebrow">YOUR FIELD DESK</span>
        <h2>
          {setup
            ? "Set up your workspace"
            : join
              ? "Join your project"
              : "Welcome back"}
        </h2>
        <p>
          {setup
            ? "Create your first Admin account."
            : invite
              ? `${invite.projectName} · ${invite.role === "admin" ? "Admin" : "Approved Worker"}`
              : "Sign in to create maps and review field feedback."}
        </p>
        {error && <Notice error>{error}</Notice>}
        {join && (
          <Button
            disabled={busy}
            onClick={() => setExistingAccount(!existingAccount)}
          >
            {existingAccount
              ? "New here? Create an account"
              : "Already have an account? Sign in to join"}
          </Button>
        )}
        <form onSubmit={submit}>
          {(setup || (join && !existingAccount)) && (
            <label>
              Your name
              <input name="name" required autoComplete="name" />
            </label>
          )}
          <label>
            Email
            <input
              type="email"
              name="email"
              required
              autoComplete="username"
              defaultValue={invite?.email}
              key={invite?.email ?? "email"}
            />
          </label>
          <label>
            Password
            <input
              type="password"
              name="password"
              required
              minLength={setup || (join && !existingAccount) ? 12 : 1}
              autoComplete={
                setup || (join && !existingAccount)
                  ? "new-password"
                  : "current-password"
              }
            />
          </label>
          {(setup || (join && !existingAccount)) && (
            <small>Use at least 12 characters.</small>
          )}
          <button className="primary" disabled={busy}>
            {busy
              ? "Working…"
              : setup
                ? "Create workspace"
                : join
                  ? existingAccount
                    ? "Sign in and join"
                    : "Create account and join"
                  : "Sign in"}
            <ArrowRight size={18} />
          </button>
        </form>
        <p className="muted">
          Opening a shared map? Use the link you were sent. Guests don’t need an
          account.
        </p>
      </section>
    </main>
  );
}
function Guest({ token, mapKey }: { token: string; mapKey: string }) {
  const [data, setData] = useState<any>(null),
    [state, setState] = useState<
      "checking" | "current" | "offline" | "unavailable"
    >("checking"),
    [locale, setLocale] = useState<Locale>(() => {
      const language = navigator.language.slice(0, 2);
      return language === "es" || language === "fr" ? language : "en";
    });
  const text = viewerText[locale];
  const status =
    state === "current" && data
      ? `${text.version} ${data.version} · ${text.updated} ${new Date(data.publishedAt).toLocaleString(locale)}`
      : text[state === "current" ? "checking" : state];
  useEffect(() => {
    let active = true,
      requestNumber = 0;
    async function refresh() {
      const current = ++requestNumber;
      try {
        const result = await api(`/view/${token}`);
        if (active && current === requestNumber) {
          setData(result);
          setState("current");
        }
      } catch (e) {
        if (active && current === requestNumber) {
          if (e instanceof ApiError && e.status === 404) {
            setData(null);
            setState("unavailable");
          } else setState("offline");
        }
      }
    }
    refresh();
    const timer = setInterval(refresh, 30000);
    const visible = () => {
      if (document.visibilityState === "visible") void refresh();
    };
    const offline = () => {
      requestNumber++;
      setState("offline");
    };
    window.addEventListener("online", refresh);
    window.addEventListener("offline", offline);
    document.addEventListener("visibilitychange", visible);
    return () => {
      active = false;
      clearInterval(timer);
      window.removeEventListener("online", refresh);
      window.removeEventListener("offline", offline);
      document.removeEventListener("visibilitychange", visible);
    };
  }, [token]);
  return (
    <main className="guest" lang={locale}>
      <header>
        <div>
          <h1>{data?.document.title ?? text.map}</h1>
          <p className={state === "current" ? "fresh" : "stale"} role="status">
            {status}
          </p>
        </div>
        <select
          aria-label={text.language}
          value={locale}
          onChange={(e) => setLocale(e.target.value as Locale)}
        >
          <option value="en">English</option>
          <option value="es">Español</option>
          <option value="fr">Français</option>
        </select>
      </header>
      {data ? (
        <>
          <MapCanvas
            document={data.document}
            mapKey={mapKey}
            locale={locale}
            photoUrl={(id) => `/api/view/${token}/photos/${id}`}
          />
          {data.document.isExample && (
            <div className="sample-ribbon">{text.sample}</div>
          )}
          <div className="guest-warning">{text.guidance}</div>
        </>
      ) : (
        <section className="empty">
          <MapIcon size={40} />
          <h2>{status}</h2>
        </section>
      )}
    </main>
  );
}
function Dashboard({
  user,
  workspaces,
  mapKey,
  onMap,
  onLogout,
}: {
  user: User;
  workspaces: any[];
  mapKey: string;
  onMap: (id: string) => void;
  onLogout: () => void;
}) {
  const [projects, setProjects] = useState<Project[]>([]),
    [active, setActive] = useState<Project | null>(null),
    [maps, setMaps] = useState<any[]>([]),
    [query, setQuery] = useState(""),
    [error, setError] = useState(""),
    [modal, setModal] = useState<"project" | "map" | "people" | null>(null),
    [invite, setInvite] = useState(""),
    [people, setPeople] = useState<any[]>([]),
    [customTemplates, setCustomTemplates] = useState<any[]>([]),
    [importing, setImporting] = useState(false),
    [importProgress, setImportProgress] = useState(""),
    [showArchived, setShowArchived] = useState(false),
    [libraryRevision, setLibraryRevision] = useState(0),
    [libraryBusy, setLibraryBusy] = useState(false),
    [mapAction, setMapAction] = useState<{
      kind: "duplicate" | "archive";
      map: any;
    } | null>(null);
  const backupInput = useRef<HTMLInputElement>(null);
  const load = async () => {
    const result = await api("/projects");
    setProjects(result.projects);
    if (!active && result.projects.length) setActive(result.projects[0]);
  };
  useEffect(() => {
    load().catch((e) => setError(e.message));
  }, []);
  useEffect(() => {
    let current = true;
    setMaps([]);
    setCustomTemplates([]);
    if (active) {
      api(
        `/projects/${active.id}/${showArchived && active.role === "admin" ? "archived-maps" : "maps"}`,
      )
        .then((r) => {
          if (current) setMaps(r.maps);
        })
        .catch((e) => {
          if (current) setError(e.message);
        });
      if (active.role === "admin")
        api(`/projects/${active.id}/templates`)
          .then((r) => {
            if (current) setCustomTemplates(r.templates);
          })
          .catch((e) => {
            if (current) setError(e.message);
          });
    }
    return () => {
      current = false;
    };
  }, [active, modal, showArchived, libraryRevision]);
  async function libraryChange(
    map: any,
    action: "duplicate" | "archive" | "unarchive",
    title?: string,
  ) {
    if (libraryBusy) return;
    setLibraryBusy(true);
    setError("");
    try {
      const result = await api(`/maps/${map.id}/${action}`, "POST", {
        ...(action !== "unarchive" ? { revision: map.revision } : {}),
        ...(title ? { title } : {}),
      });
      setMapAction(null);
      if (action === "duplicate") onMap(result.map.id);
      else setLibraryRevision((r) => r + 1);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLibraryBusy(false);
    }
  }
  async function submit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError("");
    const values = Object.fromEntries(new FormData(e.currentTarget));
    try {
      if (modal === "project") {
        const { project } = await api("/projects", "POST", {
          ...values,
          workspaceId: workspaces[0].id,
        });
        await load();
        setActive({ ...project, role: "admin" });
        setModal(null);
      } else if (modal === "map") {
        const { map } = String(values.templateId).startsWith("custom:")
          ? await api(
              `/projects/${active!.id}/templates/${String(values.templateId).slice(7)}/use`,
              "POST",
              { title: values.title },
            )
          : await api(`/projects/${active!.id}/maps`, "POST", {
              ...values,
              example: values.example === "on",
            });
        onMap(map.id);
      } else {
        const result = await api(
          `/projects/${active!.id}/invitations`,
          "POST",
          values,
        );
        setInvite(result.url);
      }
    } catch (e) {
      setError((e as Error).message);
    }
  }
  return (
    <main className="desk">
      <aside className="project-rail">
        <Brand />
        <div className="rail-heading">
          PROJECTS{" "}
          {workspaces.length > 0 && (
            <button title="New project" onClick={() => setModal("project")}>
              <Plus size={18} />
            </button>
          )}
        </div>
        <nav>
          {projects.map((p) => (
            <button
              key={p.id}
              disabled={importing || libraryBusy}
              className={active?.id === p.id ? "selected" : ""}
              onClick={() => {
                setActive(p);
                setShowArchived(false);
              }}
            >
              <Folder size={18} />
              <span>{p.name}</span>
              {p.role === "worker" && <small>Worker</small>}
            </button>
          ))}
        </nav>
        <div className="rail-bottom">
          <div className="avatar">{user.name[0]}</div>
          <div>
            <b>{user.name}</b>
            <small>{workspaces.length ? "Admin" : "Approved Worker"}</small>
          </div>
          <button
            title="Sign out"
            onClick={onLogout}
            disabled={importing || libraryBusy}
          >
            <LogOut size={18} />
          </button>
        </div>
      </aside>
      <section className="desk-main">
        <header className="desk-top">
          <span>WORKSPACE / {active?.name ?? "Projects"}</span>
          <span className="pill">Personal pilot</span>
        </header>
        <section className="desk-content">
          <div className="page-heading">
            <div>
              <span className="eyebrow">YOUR MAPS</span>
              <h1>{active?.name ?? "Make room for your first project"}</h1>
              <p>
                {active?.description ||
                  "Keep the details together. Share one clear, current view."}
              </p>
            </div>
            {active?.role === "admin" && (
              <div className="row">
                <Button
                  onClick={async () => {
                    setPeople(
                      (await api(`/projects/${active.id}/people`)).people,
                    );
                    setInvite("");
                    setModal("people");
                  }}
                >
                  <Users size={17} />
                  People
                </Button>
                <Button
                  disabled={importing}
                  onClick={() => backupInput.current?.click()}
                >
                  Import backup
                </Button>
                <input
                  ref={backupInput}
                  type="file"
                  accept=".zip,application/zip"
                  hidden
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    e.target.value = "";
                    if (!file || !active || importing) return;
                    setImporting(true);
                    setError("");
                    setImportProgress("Reading private map backup…");
                    try {
                      const restored = await importBackup(
                        file,
                        active.id,
                        setImportProgress,
                      );
                      onMap(restored.id);
                    } catch (error) {
                      setError((error as Error).message);
                    } finally {
                      setImporting(false);
                      setImportProgress("");
                    }
                  }}
                />
                <Button
                  disabled={importing}
                  primary
                  onClick={() => setModal("map")}
                >
                  <Plus size={18} />
                  New map
                </Button>
              </div>
            )}
          </div>
          {error && <Notice error>{error}</Notice>}
          {importProgress && <Notice>{importProgress}</Notice>}
          {active?.role === "admin" && (
            <div className="row">
              <Button
                disabled={libraryBusy || importing}
                onClick={() => setShowArchived(!showArchived)}
              >
                {showArchived ? "Back to current maps" : "Archived maps"}
              </Button>
              {showArchived && (
                <span className="muted">
                  Restored maps keep their public links paused.
                </span>
              )}
            </div>
          )}
          <label className="search">
            <Search size={18} />
            <input
              placeholder="Find a map"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </label>
          <div className="map-grid">
            {maps
              .filter((m) =>
                m.title.toLowerCase().includes(query.toLowerCase()),
              )
              .map((m, i) => (
                <article className="map-card" key={m.id}>
                  <button
                    className="map-card-open"
                    disabled={showArchived || libraryBusy || importing}
                    onClick={() => onMap(m.id)}
                  >
                    <div className={`card-art art-${i % 4}`}>
                      <div className="art-line" />
                      <span className="art-pin">{i % 2 ? "A" : "↥"}</span>
                      <span className="art-label">
                        {showArchived
                          ? "ARCHIVED"
                          : m.status === "published"
                            ? "LATEST VIEW"
                            : "WORK IN PROGRESS"}
                      </span>
                    </div>
                    <div className="card-body">
                      <span className={`status ${m.status}`}>
                        {showArchived
                          ? "Archived · link paused"
                          : m.status === "published"
                            ? `Published · v${m.publication}`
                            : m.status === "suspended"
                              ? "Link paused"
                              : "Private draft"}
                      </span>
                      <h3>{m.title}</h3>
                      <div className="card-meta">
                        <span>Edited {date(m.updatedAt)}</span>
                        <ChevronRight size={18} />
                      </div>
                    </div>
                  </button>
                  {active?.role === "admin" && (
                    <div className="map-card-actions">
                      {showArchived ? (
                        <Button
                          disabled={libraryBusy || importing}
                          onClick={() => libraryChange(m, "unarchive")}
                        >
                          Restore map
                        </Button>
                      ) : (
                        <>
                          <Button
                            disabled={libraryBusy || importing}
                            onClick={() =>
                              setMapAction({ kind: "duplicate", map: m })
                            }
                          >
                            Duplicate
                          </Button>
                          <Button
                            disabled={libraryBusy || importing}
                            onClick={() =>
                              setMapAction({ kind: "archive", map: m })
                            }
                          >
                            Archive
                          </Button>
                        </>
                      )}
                    </div>
                  )}
                </article>
              ))}
            {active?.role === "admin" && !showArchived && (
              <button className="new-map-card" onClick={() => setModal("map")}>
                <Plus size={28} />
                <h3>Start something useful</h3>
                <p>Choose a template or a blank map.</p>
              </button>
            )}
          </div>
          <div className="desk-note">
            <Flag size={20} />
            <p>
              <b>Made for the people doing the work.</b>
              <br />
              Your drawings give context. Published links give everyone the same
              view.
            </p>
          </div>
        </section>
      </section>
      {mapAction && (
        <Dialog
          title={
            mapAction.kind === "duplicate" ? "Duplicate map" : "Archive map"
          }
          close={() => {
            if (!libraryBusy) setMapAction(null);
          }}
        >
          {error && <Notice error>{error}</Notice>}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              const title = new FormData(e.currentTarget).get("title");
              void libraryChange(
                mapAction.map,
                mapAction.kind,
                title ? String(title) : undefined,
              );
            }}
          >
            {mapAction.kind === "duplicate" ? (
              <>
                <p>
                  Copy the saved draft and its photos into a new private map.
                  The original map and link stay unchanged.
                </p>
                <label>
                  New map name
                  <input
                    name="title"
                    required
                    maxLength={120}
                    defaultValue={`Copy of ${mapAction.map.title}`.slice(
                      0,
                      120,
                    )}
                  />
                </label>
              </>
            ) : (
              <p>
                Archive “{mapAction.map.title}”? Its shared link stops working.
                Your draft and published history stay saved, and you can restore
                the map later.
              </p>
            )}
            <button className="primary" disabled={libraryBusy}>
              {libraryBusy
                ? "Working…"
                : mapAction.kind === "duplicate"
                  ? "Create private copy"
                  : "Archive map"}
            </button>
          </form>
        </Dialog>
      )}
      {modal && (
        <Dialog
          title={
            modal === "project"
              ? "New project"
              : modal === "map"
                ? "Start a map"
                : "Project people"
          }
          close={() => setModal(null)}
        >
          {error && <Notice error>{error}</Notice>}
          <form onSubmit={submit}>
            {modal === "project" ? (
              <>
                <label>
                  Project name
                  <input
                    name="name"
                    required
                    placeholder="West yard, September event…"
                  />
                </label>
                <label>
                  Description
                  <textarea
                    name="description"
                    placeholder="A little context for your team"
                  />
                </label>
              </>
            ) : modal === "map" ? (
              <>
                <label>
                  Map name
                  <input
                    name="title"
                    required
                    placeholder="North entrance and storage"
                  />
                </label>
                <label>
                  Starting point
                  <select name="templateId">
                    {templates.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name}
                      </option>
                    ))}
                    {customTemplates.map((t) => (
                      <option key={t.id} value={`custom:${t.id}`}>
                        My template · {t.name}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="check">
                  <input type="checkbox" name="example" />
                  Include fictional example markings
                </label>
                <p className="muted">
                  Every template uses the same simple drawing tools. Make it
                  your own.
                </p>
              </>
            ) : (
              <>
                <p>
                  Each person gets their own password. Guests only need the
                  published map link.
                </p>
                <label>
                  Email
                  <input type="email" name="email" required />
                </label>
                <label>
                  Access
                  <select name="role">
                    <option value="worker">
                      Approved Worker — view and send private feedback
                    </option>
                    <option value="admin">
                      Admin — edit, review and publish
                    </option>
                  </select>
                </label>
              </>
            )}
            <button className="primary">
              {modal === "people"
                ? "Create invitation link"
                : modal === "map"
                  ? "Create map"
                  : "Create project"}
            </button>
          </form>
          {invite && (
            <div className="invite-result">
              <b>Send this link yourself</b>
              <input value={invite} readOnly />
              <Button onClick={() => navigator.clipboard.writeText(invite)}>
                <Copy size={16} />
                Copy invitation
              </Button>
              <small>Single use · Expires in 7 days</small>
            </div>
          )}
          {modal === "people" &&
            people.map((p) => (
              <div className="person" key={p.id}>
                <div>
                  {p.name}
                  <small>
                    {p.email} · {p.role}
                  </small>
                </div>
                <Button
                  onClick={async () => {
                    await api(
                      `/projects/${active!.id}/people/${p.id}`,
                      "DELETE",
                      {},
                    );
                    setPeople(people.filter((x) => x.id !== p.id));
                  }}
                >
                  Remove access
                </Button>
              </div>
            ))}
        </Dialog>
      )}
    </main>
  );
}
const tools = [
  { kind: "select", label: "Select", Icon: MousePointer2 },
  { kind: "point", label: "Point", Icon: MapPin },
  { kind: "structure", label: "Number", Icon: Type },
  { kind: "gate", label: "Entrance", Icon: Flag },
  { kind: "arrow", label: "Arrow", Icon: MoveRight },
  { kind: "approved-road", label: "Good road", Icon: Route },
  { kind: "wrong-road", label: "Bad road", Icon: TriangleAlert },
  { kind: "stop", label: "Stop", Icon: X },
  { kind: "label", label: "Label", Icon: Type },
  { kind: "line", label: "Line", Icon: PenLine },
  { kind: "freehand", label: "Draw", Icon: PenLine },
  { kind: "area", label: "Area", Icon: Square },
  { kind: "reference-line", label: "Private guide", Icon: Lock },
];
const pointKinds = new Set([
  "point",
  "structure",
  "gate",
  "stop",
  "arrow",
  "label",
]);
function Editor({
  mapId,
  user,
  mapKey,
  back,
}: {
  mapId: string;
  user: User;
  mapKey: string;
  back: () => void;
}) {
  const [record, setRecord] = useState<any>(null),
    [doc, setDoc] = useState<MapDocument | null>(null),
    [base, setBase] = useState<MapDocument | null>(null),
    [tool, setTool] = useState("select"),
    [selected, setSelected] = useState(""),
    [drawing, setDrawing] = useState<Coordinate[]>([]),
    [tab, setTab] = useState("marking"),
    [error, setError] = useState(""),
    [message, setMessage] = useState(""),
    [busy, setBusy] = useState(false),
    [preview, setPreview] = useState(false),
    [conflicts, setConflicts] = useState<Conflict[]>([]),
    [undo, setUndo] = useState<MapDocument[]>([]),
    [redo, setRedo] = useState<MapDocument[]>([]),
    [feedback, setFeedback] = useState<any[]>([]),
    [versions, setVersions] = useState<any[]>([]),
    [qr, setQR] = useState(""),
    [workerNote, setWorkerNote] = useState(""),
    [workerFeatures, setWorkerFeatures] = useState<MapFeature[]>([]),
    [workerPhotos, setWorkerPhotos] = useState<string[]>([]),
    [syncReports, setSyncReports] = useState<any>(null),
    [placeQuery, setPlaceQuery] = useState(""),
    [places, setPlaces] = useState<any[]>([]),
    [focus, setFocus] = useState<
      { center: Coordinate; zoom: number; nonce: number } | undefined
    >(),
    [coordinateInput, setCoordinateInput] = useState(""),
    [starterLabel, setStarterLabel] = useState("");
  const view = useRef<{ center: Coordinate; zoom: number } | null>(null),
    docRef = useRef(doc);
  docRef.current = doc;
  const admin = record?.role === "admin",
    dirty = !!doc && !!base && JSON.stringify(doc) !== JSON.stringify(base);
  async function load() {
    const { map } = await api(`/maps/${mapId}`);
    setRecord(map);
    const document = adminDoc(map);
    setDoc(document);
    setBase(document);
    if (map.role === "admin") {
      const cached = await getDraft(user.id, mapId);
      if (cached) {
        const merged = recoverDraft(cached, document);
        setDoc(merged.document);
        setConflicts(merged.conflicts);
        setMessage("Recovered your saved work from this device.");
      }
    }
  }
  function adminDoc(map: any): MapDocument {
    return map.role === "admin"
      ? map.draft
      : mapDocumentSchema.parse({
          ...map.payload,
          features: map.payload.features.map((f: any) => ({
            ...f,
            visibility: "public",
          })),
        });
  }
  useEffect(() => {
    load().catch((e) => setError(e.message));
  }, [mapId]);
  useEffect(() => {
    if (admin && (dirty || conflicts.length) && doc && base) {
      const timer = setTimeout(() => {
        putDraft(user.id, mapId, {
          base,
          document: doc,
          revision: record.revision,
          conflicts,
          savedAt: new Date().toISOString(),
        }).catch(() =>
          setError(
            "This device could not keep a recovery copy. Save to the server before closing.",
          ),
        );
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [doc, base, admin, conflicts]);
  useEffect(() => {
    const guard = (event: BeforeUnloadEvent) => {
      if (dirty || workerFeatures.length || workerNote) {
        event.preventDefault();
        event.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", guard);
    return () => window.removeEventListener("beforeunload", guard);
  }, [dirty, workerFeatures, workerNote]);
  useEffect(() => {
    if (!record) return;
    if (tab === "feedback")
      api(`/maps/${mapId}/submissions`)
        .then((r) => setFeedback(r.submissions))
        .catch((e) => setError(e.message));
    if (tab === "history" && admin)
      api(`/maps/${mapId}/history`)
        .then((r) => setVersions(r.history))
        .catch((e) => setError(e.message));
    if (tab === "share" && admin)
      QRCode.toDataURL(`${location.origin}/v/${record.shareToken}`, {
        width: 480,
        margin: 2,
        color: { dark: "#183d34", light: "#ffffff" },
      }).then(setQR);
    if (tab === "settings" && admin)
      api(`/maps/${mapId}/sync-reports`)
        .then(setSyncReports)
        .catch((e) => setError(e.message));
  }, [tab, record?.publication]);
  function change(next: MapDocument) {
    if (!doc) return;
    setUndo((history) => [...history.slice(-39), doc]);
    setRedo([]);
    setDoc(next);
    setMessage("");
  }
  function setFeature(patch: Partial<MapFeature>) {
    if (!doc) return;
    if (admin)
      change({
        ...doc,
        features: doc.features.map((f) =>
          f.id === selected ? { ...f, ...patch } : f,
        ),
      });
    else
      setWorkerFeatures((fs) =>
        fs.map((f) => (f.id === selected ? { ...f, ...patch } : f)),
      );
  }
  function addFeature(points: Coordinate[]) {
    if (!doc) return;
    const color =
      tool === "wrong-road"
        ? "#e48b1c"
        : tool === "stop"
          ? "#d62b2b"
          : tool === "reference-line"
            ? "#7c687b"
            : "#23864a";
    const geometry: any = pointKinds.has(tool)
      ? { type: "Point", coordinates: points[0] }
      : tool === "area"
        ? { type: "Polygon", coordinates: [[...points, points[0]]] }
        : { type: "LineString", coordinates: points };
    const feature = featureSchema.parse({
      id: crypto.randomUUID(),
      kind: tool,
      label:
        starterLabel ||
        (tool === "structure"
          ? "#"
          : tool === "gate"
            ? "Entrance"
            : tool === "stop"
              ? ""
              : tool === "label"
                ? "Your label"
                : ""),
      geometry,
      color,
      visibility: tool === "reference-line" ? "private" : "public",
      warning:
        tool === "wrong-road"
          ? "bad-road"
          : tool === "stop"
            ? "do-not-cross"
            : "none",
    });
    if (admin) change({ ...doc, features: [...doc.features, feature] });
    else setWorkerFeatures([...workerFeatures, feature]);
    setStarterLabel("");
    setSelected(feature.id);
    setTool("select");
    setDrawing([]);
    setTab("marking");
  }
  async function save() {
    if (!doc || !admin) return null;
    if (conflicts.length) throw Error("Resolve the overlapping edits first.");
    const document = mapDocumentSchema.parse(doc);
    try {
      const { map } = await api(`/maps/${mapId}/draft`, "PUT", {
        revision: record.revision,
        document,
      });
      setRecord({ ...map, role: "admin" });
      setBase(map.draft);
      setDoc(map.draft);
      await clearDraft(user.id, mapId);
      setMessage("Draft saved. Guest map is unchanged.");
      return map;
    } catch (e) {
      if (e instanceof ApiError && e.status === 409 && e.details) {
        const merged = mergeDrafts(base!, doc, e.details.document);
        setBase(e.details.document);
        setRecord({
          ...record,
          revision: e.details.revision,
          draft: e.details.document,
        });
        setDoc(merged.document);
        setConflicts(merged.conflicts);
        setMessage(
          merged.conflicts.length
            ? "Choose which overlapping edits to keep."
            : "Independent changes merged. Review and save again.",
        );
      }
      throw e;
    }
  }
  async function run(task: () => Promise<unknown>) {
    setBusy(true);
    setError("");
    try {
      await task();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  async function startPreview() {
    const saved = dirty ? await save() : record;
    if (!saved) return;
    publishDocument(saved.draft);
    setPreview(true);
  }
  async function submitFeedback() {
    if (!workerNote.trim())
      throw Error("Add a note so the Admin knows what needs attention.");
    const payload = {
      clientId: crypto.randomUUID(),
      mapId,
      basePublication: record.version,
      note: workerNote,
      features: workerFeatures,
      attachmentIds: workerPhotos,
    };
    await queueFeedback(user.id, payload);
    setWorkerFeatures([]);
    setWorkerPhotos([]);
    setWorkerNote("");
    setMessage("Feedback saved on this device. Sending…");
    try {
      await api("/submissions", "POST", payload);
      await clearFeedback(user.id, payload.clientId);
      setMessage(
        "Sent privately to the Admin. The published map is unchanged.",
      );
    } catch (e) {
      setMessage(
        "Feedback is saved on this device. Use Retry saved feedback when connected.",
      );
      throw e;
    }
  }
  async function retryFeedback() {
    const queue = await feedbackQueue(user.id);
    for (const row of queue) {
      await api("/submissions", "POST", row.payload);
      await clearFeedback(user.id, row.payload.clientId);
    }
    setMessage("All saved feedback sent.");
  }
  const chosen = (admin ? doc?.features : workerFeatures)?.find(
    (f) => f.id === selected,
  );
  const renderedDoc = doc
    ? {
        ...doc,
        features: admin ? doc.features : [...doc.features, ...workerFeatures],
      }
    : null;
  if (!record || !doc || !renderedDoc)
    return (
      <div className="empty">
        {error ? <Notice error>{error}</Notice> : "Opening map…"}
        <Button onClick={back}>Back to projects</Button>
      </div>
    );
  return (
    <main className="editor">
      <header className="editor-top">
        <button
          onClick={() => {
            if (!dirty) back();
            else setError("Save your draft before leaving this map.");
          }}
          title="Projects"
        >
          <ArrowLeft />
        </button>
        <div className="editor-title">
          <input
            aria-label="Map title"
            value={doc.title}
            disabled={!admin || busy}
            onChange={(e) => change({ ...doc, title: e.target.value })}
          />
          <small>
            {admin
              ? `${dirty ? "Unsaved changes" : "Draft saved"} · Published ${record.publication ? "v" + record.publication : "never"}`
              : `Approved Worker · Version ${record.version}`}
          </small>
        </div>
        <div className="editor-actions">
          {admin ? (
            <>
              <Button
                disabled={busy || !dirty || !!conflicts.length}
                onClick={() => run(save)}
              >
                <Save size={17} />
                <span>Save draft</span>
              </Button>
              <Button
                primary
                disabled={busy || !!conflicts.length}
                onClick={() => run(startPreview)}
              >
                <Eye size={17} />
                <span>Review & publish</span>
              </Button>
            </>
          ) : (
            <Button primary disabled={busy} onClick={() => run(submitFeedback)}>
              <Send size={17} />
              Send feedback
            </Button>
          )}
        </div>
      </header>
      <div className="editor-body">
        <nav className="tool-rail" aria-label="Drawing tools">
          {tools
            .filter(
              (t) =>
                admin ||
                [
                  "select",
                  "point",
                  "arrow",
                  "label",
                  "line",
                  "area",
                  "freehand",
                ].includes(t.kind),
            )
            .map((t) => (
              <button
                key={t.kind}
                disabled={busy}
                className={tool === t.kind ? "active" : ""}
                title={t.label}
                aria-label={t.label}
                onClick={() => {
                  setTool(t.kind);
                  setStarterLabel("");
                  setDrawing([]);
                }}
              >
                <t.Icon size={21} />
                <span>{t.label}</span>
              </button>
            ))}
          {admin && (
            <div className="undo-row">
              <button
                aria-label="Undo"
                disabled={!undo.length || busy}
                onClick={() => {
                  setRedo([doc, ...redo]);
                  setDoc(undo.at(-1)!);
                  setUndo(undo.slice(0, -1));
                }}
              >
                <Undo2 size={18} />
              </button>
              <button
                aria-label="Redo"
                disabled={!redo.length || busy}
                onClick={() => {
                  setUndo([...undo, doc]);
                  setDoc(redo[0]);
                  setRedo(redo.slice(1));
                }}
              >
                <Redo2 size={18} />
              </button>
            </div>
          )}
        </nav>
        <section className="editor-map">
          <MapCanvas
            document={renderedDoc}
            mapKey={mapKey}
            selected={selected}
            editable={!busy}
            editableIds={admin ? undefined : workerFeatures.map((f) => f.id)}
            onSelect={busy ? undefined : setSelected}
            focus={focus}
            onStroke={tool === "freehand" && !busy ? addFeature : undefined}
            onPoint={
              busy || tool === "select" || tool === "freehand"
                ? undefined
                : (point) =>
                    pointKinds.has(tool)
                      ? addFeature([point])
                      : setDrawing([...drawing, point])
            }
            onMove={(id, point) => {
              setSelected(id);
              if (admin)
                change({
                  ...doc,
                  features: doc.features.map((f) =>
                    f.id === id
                      ? {
                          ...f,
                          geometry: { type: "Point", coordinates: point },
                        }
                      : f,
                  ),
                });
              else
                setWorkerFeatures((fs) =>
                  fs.map((f) =>
                    f.id === id
                      ? {
                          ...f,
                          geometry: { type: "Point", coordinates: point },
                        }
                      : f,
                  ),
                );
            }}
            onGeometryChange={(id, geometry) => {
              setSelected(id);
              if (admin)
                change({
                  ...doc,
                  features: doc.features.map((f) =>
                    f.id === id ? { ...f, geometry } : f,
                  ),
                });
              else
                setWorkerFeatures((fs) =>
                  fs.map((f) => (f.id === id ? { ...f, geometry } : f)),
                );
            }}
            onView={(center, zoom) => {
              view.current = { center, zoom };
            }}
            drawing={drawing}
          />
          {admin && (
            <div className="place-search">
              <form
                onSubmit={(event) => {
                  event.preventDefault();
                  run(async () => {
                    const pair = placeQuery
                      .split(/[ ,]+/)
                      .filter(Boolean)
                      .map(Number);
                    if (
                      pair.length === 2 &&
                      pair.every(Number.isFinite) &&
                      Math.abs(pair[0]) <= 85 &&
                      Math.abs(pair[1]) <= 180
                    ) {
                      setFocus({
                        center: [pair[1], pair[0]],
                        zoom: 17,
                        nonce: Date.now(),
                      });
                      setPlaces([]);
                      return;
                    }
                    if (!mapKey)
                      throw Error(
                        "Configure the map provider to search places.",
                      );
                    const response = await fetch(
                      `https://api.maptiler.com/geocoding/${encodeURIComponent(placeQuery.trim())}.json?key=${encodeURIComponent(mapKey)}&limit=5`,
                    );
                    if (!response.ok)
                      throw Error(
                        "Place search is unavailable. You can paste latitude, longitude instead.",
                      );
                    const result = await response.json();
                    setPlaces(result.features ?? []);
                    if (!result.features?.length)
                      setMessage("No matching places found.");
                  });
                }}
              >
                <input
                  aria-label="Find a place or latitude, longitude"
                  placeholder="Find a place or paste lat, lon"
                  value={placeQuery}
                  onChange={(e) => setPlaceQuery(e.target.value)}
                />
                <button
                  type="submit"
                  disabled={busy || !placeQuery.trim()}
                  aria-label="Find place"
                >
                  <Search size={18} />
                </button>
              </form>
              {!!places.length && (
                <div className="place-results">
                  {places.map((place) => (
                    <button
                      key={place.id}
                      onClick={() => {
                        setFocus({
                          center: place.center,
                          zoom: 16,
                          nonce: Date.now(),
                        });
                        setPlaces([]);
                      }}
                    >
                      {place.place_name}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          {tool !== "select" && (
            <div className="drawing-hint">
              <b>
                {tool === "freehand"
                  ? "Drag to draw. Release to finish."
                  : pointKinds.has(tool)
                    ? "Tap the map to place a marking"
                    : "Tap along the shape you want to draw"}
              </b>
              {!pointKinds.has(tool) && tool !== "freehand" && (
                <button
                  disabled={drawing.length < (tool === "area" ? 3 : 2)}
                  onClick={() => addFeature(drawing)}
                >
                  Finish drawing <Check size={16} />
                </button>
              )}
              <button
                onClick={() => {
                  setTool("select");
                  setDrawing([]);
                }}
              >
                Cancel
              </button>
            </div>
          )}
          {doc.isExample && (
            <div className="sample-ribbon">FICTIONAL EXAMPLE</div>
          )}
        </section>
        <aside className="inspector">
          <nav className="panel-tabs">
            {[
              { id: "marking", Icon: MapPin, label: "Marks" },
              { id: "feedback", Icon: MessageSquare, label: "Feedback" },
              ...(admin
                ? [
                    { id: "share", Icon: Share2, label: "Share" },
                    { id: "history", Icon: History, label: "History" },
                    { id: "settings", Icon: Settings, label: "Settings" },
                  ]
                : []),
            ].map((t) => (
              <button
                key={t.id}
                className={tab === t.id ? "active" : ""}
                onClick={() => setTab(t.id)}
                title={t.label}
              >
                <t.Icon size={18} />
                <span>{t.label}</span>
              </button>
            ))}
          </nav>
          <fieldset className="panel-content inspector-fields" disabled={busy}>
            {error && <Notice error>{error}</Notice>}
            {message && <Notice>{message}</Notice>}
            {tab === "marking" && admin && !!doc.starterLabels?.length && (
              <details>
                <summary>Quick labels</summary>
                <div className="quick-labels">
                  {doc.starterLabels.map((label) => (
                    <Button
                      key={label}
                      onClick={() => {
                        setStarterLabel(label);
                        setTool("point");
                        setDrawing([]);
                        setMessage(`Tap where “${label}” belongs.`);
                      }}
                    >
                      {label}
                    </Button>
                  ))}
                </div>
                <label>
                  Customize this kit · one label per line
                  <textarea
                    value={doc.starterLabels.join("\n")}
                    onChange={(e) =>
                      change({
                        ...doc,
                        starterLabels: e.target.value.split("\n").slice(0, 100),
                      })
                    }
                  />
                </label>
              </details>
            )}
            {tab === "marking" && (
              <>
                {!admin && (
                  <section className="field-note">
                    <h3>Your private feedback</h3>
                    <label>
                      Explain your marking
                      <textarea
                        value={workerNote}
                        onChange={(e) => setWorkerNote(e.target.value)}
                        placeholder="What changed? What should the Admin check?"
                      />
                    </label>
                    <label>
                      Photos
                      <input
                        type="file"
                        accept="image/*"
                        disabled={busy || workerPhotos.length >= 8}
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file)
                            run(async () => {
                              const id = await uploadPhoto(
                                file,
                                record.projectId,
                              );
                              setWorkerPhotos((ids) => [...ids, id]);
                            });
                          e.target.value = "";
                        }}
                      />
                    </label>
                    <div className="photo-strip">
                      {workerPhotos.map((id) => (
                        <div key={id}>
                          <img
                            src={`/api/photos/${id}`}
                            alt="Private feedback photo"
                          />
                          <button
                            onClick={() =>
                              setWorkerPhotos((ids) =>
                                ids.filter((x) => x !== id),
                              )
                            }
                            aria-label="Remove photo from feedback"
                          >
                            <X size={12} />
                          </button>
                        </div>
                      ))}
                    </div>
                    <Button onClick={() => run(retryFeedback)}>
                      Retry saved feedback
                    </Button>
                  </section>
                )}
                {tool !== "select" && tool !== "freehand" && (
                  <details>
                    <summary>Place from coordinates</summary>
                    <label>
                      Latitude, longitude · one pair per line
                      <textarea
                        value={coordinateInput}
                        onChange={(e) => setCoordinateInput(e.target.value)}
                        placeholder="39.091006, -104.107599"
                      />
                    </label>
                    <Button
                      onClick={() => {
                        try {
                          const points = coordinateInput
                            .trim()
                            .split(/\n+/)
                            .map((line) => {
                              const parts = line
                                .trim()
                                .split(/[,\s]+/)
                                .map(Number);
                              if (
                                parts.length !== 2 ||
                                parts.some((n) => !Number.isFinite(n)) ||
                                Math.abs(parts[0]) > 85 ||
                                Math.abs(parts[1]) > 180
                              )
                                throw Error(
                                  "Use latitude, longitude on each line. West longitudes must be negative.",
                                );
                              return [parts[1], parts[0]] as Coordinate;
                            });
                          if (
                            points.length <
                            (pointKinds.has(tool) ? 1 : tool === "area" ? 3 : 2)
                          )
                            throw Error("Add enough points for this marking.");
                          if (pointKinds.has(tool) && points.length !== 1)
                            throw Error("Place one point at a time.");
                          addFeature(points);
                          setCoordinateInput("");
                        } catch (e) {
                          setError((e as Error).message);
                        }
                      }}
                    >
                      Place marking
                    </Button>
                  </details>
                )}
                {chosen ? (
                  <>
                    <span className="eyebrow">
                      {chosen.visibility === "private"
                        ? "PRIVATE REFERENCE"
                        : admin
                          ? "MAP MARKING"
                          : "YOUR FEEDBACK MARK"}
                    </span>
                    <h2>
                      {tools.find((t) => t.kind === chosen.kind)?.label ??
                        "Marking"}
                    </h2>
                    <label>
                      Label
                      <input
                        value={chosen.label}
                        onChange={(e) => setFeature({ label: e.target.value })}
                      />
                    </label>
                    <div className="two-col">
                      <label>
                        Color
                        <input
                          type="color"
                          value={chosen.color}
                          onChange={(e) =>
                            setFeature({ color: e.target.value })
                          }
                        />
                      </label>
                      {chosen.kind === "arrow" ? (
                        <label>
                          Direction
                          <input
                            type="range"
                            min="0"
                            max="359"
                            value={chosen.rotation}
                            onChange={(e) =>
                              setFeature({ rotation: Number(e.target.value) })
                            }
                          />
                        </label>
                      ) : (
                        <label>
                          Thickness
                          <input
                            type="range"
                            min="1"
                            max="24"
                            value={chosen.width}
                            onChange={(e) =>
                              setFeature({ width: Number(e.target.value) })
                            }
                          />
                        </label>
                      )}
                    </div>
                    {admin && (
                      <>
                        <label>
                          Warning
                          <select
                            value={chosen.warning}
                            onChange={(e) =>
                              setFeature({ warning: e.target.value as any })
                            }
                          >
                            <option value="none">No warning</option>
                            <option value="do-not-cross">
                              DO NOT CROSS — 3 languages
                            </option>
                            <option value="no-access">
                              NO ACCESS — 3 languages
                            </option>
                            <option value="bad-road">
                              DO NOT USE THIS ROAD — 3 languages
                            </option>
                          </select>
                        </label>
                        <label>
                          Visible to
                          <select
                            disabled={chosen.kind === "reference-line"}
                            value={chosen.visibility}
                            onChange={(e) =>
                              setFeature({ visibility: e.target.value as any })
                            }
                          >
                            <option value="public">
                              Everyone in the published map
                            </option>
                            <option value="private">Admin only</option>
                          </select>
                        </label>
                        <label>
                          Note for map viewers
                          <textarea
                            value={chosen.publicNote}
                            onChange={(e) =>
                              setFeature({ publicNote: e.target.value })
                            }
                          />
                        </label>
                        <label>
                          Photos for this marking
                          <input
                            type="file"
                            accept="image/*"
                            disabled={busy || chosen.attachmentIds.length >= 8}
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file)
                                run(async () => {
                                  const id = await uploadPhoto(
                                    file,
                                    record.projectId,
                                  );
                                  setFeature({
                                    attachmentIds: [
                                      ...chosen.attachmentIds,
                                      id,
                                    ],
                                  });
                                });
                              e.target.value = "";
                            }}
                          />
                        </label>
                        <div className="photo-strip">
                          {chosen.attachmentIds.map((id) => (
                            <div key={id}>
                              <img
                                src={`/api/photos/${id}`}
                                alt="Marking photo"
                              />
                              <button
                                onClick={() =>
                                  setFeature({
                                    attachmentIds: chosen.attachmentIds.filter(
                                      (x) => x !== id,
                                    ),
                                  })
                                }
                                aria-label="Remove photo from marking"
                              >
                                <X size={12} />
                              </button>
                            </div>
                          ))}
                        </div>
                        <details>
                          <summary>Admin details & translations</summary>
                          <label>
                            Private note
                            <textarea
                              value={chosen.privateNote}
                              onChange={(e) =>
                                setFeature({ privateNote: e.target.value })
                              }
                            />
                          </label>
                          <label>
                            Source
                            <input
                              value={chosen.source}
                              onChange={(e) =>
                                setFeature({ source: e.target.value })
                              }
                            />
                          </label>
                          <label>
                            Review
                            <select
                              value={chosen.reviewState}
                              onChange={(e) =>
                                setFeature({
                                  reviewState: e.target.value as any,
                                })
                              }
                            >
                              <option value="unreviewed">Unreviewed</option>
                              <option value="needs-review">Needs review</option>
                              <option value="checked">Checked</option>
                            </select>
                          </label>
                          {(["en", "es", "fr"] as const).map((lang) => (
                            <label key={lang}>
                              {lang === "en"
                                ? "English"
                                : lang === "es"
                                  ? "Español"
                                  : "Français"}
                              <input
                                value={chosen.translations[lang] ?? ""}
                                onChange={(e) =>
                                  setFeature({
                                    translations: {
                                      ...chosen.translations,
                                      [lang]: e.target.value,
                                    },
                                  })
                                }
                              />
                            </label>
                          ))}
                        </details>
                      </>
                    )}
                    <Button
                      onClick={() => {
                        if (admin)
                          change({
                            ...doc,
                            features: doc.features.filter(
                              (f) => f.id !== selected,
                            ),
                          });
                        else
                          setWorkerFeatures((fs) =>
                            fs.filter((f) => f.id !== selected),
                          );
                        setSelected("");
                      }}
                    >
                      <Trash2 size={16} />
                      Remove marking
                    </Button>
                  </>
                ) : (
                  <>
                    <span className="eyebrow">MAKE IT CLEAR</span>
                    <h2>Mark what matters.</h2>
                    <p>
                      Pick a tool, then tap the map. Select a marking to change
                      it.
                    </p>
                    <div className="mark-list">
                      {(admin ? doc.features : workerFeatures).map((f) => (
                        <button key={f.id} onClick={() => setSelected(f.id)}>
                          <span style={{ background: f.color }} />
                          {f.label || f.kind}
                          {f.visibility === "private" && <Lock size={13} />}
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </>
            )}
            {tab === "share" && admin && (
              <>
                <span className="eyebrow">ONE LINK. LATEST VIEW.</span>
                <h2>Share this map</h2>
                <p>Guests can view the published map without a login.</p>
                {!record.publication ? (
                  <Notice>
                    Publish a reviewed draft to activate this link.
                  </Notice>
                ) : (
                  <span className={`status ${record.status}`}>
                    {record.status === "suspended"
                      ? "Link paused"
                      : `Live · Version ${record.publication}`}
                  </span>
                )}
                {qr && (
                  <img className="qr" src={qr} alt="QR code for this map" />
                )}
                <input
                  aria-label="Guest map link"
                  readOnly
                  value={`${location.origin}/v/${record.shareToken}`}
                />
                <div className="row">
                  <Button
                    onClick={() =>
                      navigator.clipboard
                        .writeText(`${location.origin}/v/${record.shareToken}`)
                        .then(() => setMessage("Link copied."))
                    }
                  >
                    <Copy size={16} />
                    Copy link
                  </Button>
                  <a className="button" href={qr} download="waytogether-current-map-qr.png">
                    <Download size={16} />
                    QR
                  </a>
                </div>
                <Button
                  disabled={busy || record.status !== "published"}
                  onClick={() =>
                    run(async () => {
                      await downloadQRSign(
                        doc.title,
                        `${location.origin}/v/${record.shareToken}`,
                      );
                      setMessage(
                        "Printable QR sign downloaded. The sign opens this map’s current publication.",
                      );
                    })
                  }
                >
                  Download printable sign
                </Button>
                <a
                  className="text-link"
                  href={`/v/${record.shareToken}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  Open Guest view ↗
                </a>
                <p className="muted">
                  Anyone with this link can see its published contents. Private
                  notes and guides stay out.
                </p>
                {!!record.publication && (
                  <Button
                    onClick={() =>
                      run(async () => {
                        const status =
                          record.status === "suspended"
                            ? "published"
                            : "suspended";
                        await api(`/maps/${mapId}/settings`, "PATCH", {
                          status,
                        });
                        setRecord({ ...record, status });
                      })
                    }
                  >
                    {record.status === "suspended"
                      ? "Resume Guest link"
                      : "Pause Guest link"}
                  </Button>
                )}
              </>
            )}
            {tab === "feedback" && (
              <>
                <h2>Field feedback</h2>
                <p>Private notes from the people on site.</p>
                {!feedback.length && <p className="muted">No feedback yet.</p>}
                {feedback.map((f) => (
                  <article className="feedback-card" key={f.id}>
                    <span className="status">
                      {f.status} · v{f.payload.basePublication}
                    </span>
                    <p>{f.payload.note}</p>
                    <div className="photo-strip">
                      {f.payload.attachmentIds.map((id: string) => (
                        <a
                          key={id}
                          href={`/api/photos/${id}`}
                          target="_blank"
                          rel="noreferrer"
                        >
                          <img
                            src={`/api/photos/${id}`}
                            alt="Private field photo"
                          />
                        </a>
                      ))}
                    </div>
                    <small>
                      {f.payload.features.length} markings · {date(f.createdAt)}
                    </small>
                    {admin && f.status === "pending" && (
                      <div className="row">
                        <Button
                          disabled={busy || dirty}
                          onClick={() =>
                            run(async () => {
                              const r = await api(
                                `/submissions/${f.id}/review`,
                                "POST",
                                { action: "apply", revision: record.revision },
                              );
                              setRecord({ ...r.map, role: "admin" });
                              setDoc(r.map.draft);
                              setBase(r.map.draft);
                              setFeedback(
                                feedback.map((x) =>
                                  x.id === f.id
                                    ? { ...x, status: "applied" }
                                    : x,
                                ),
                              );
                              setMessage(
                                "Added to private draft. Review the markings before publishing.",
                              );
                            })
                          }
                        >
                          Add to draft
                        </Button>
                        <Button
                          disabled={busy || dirty}
                          onClick={() =>
                            run(async () => {
                              await api(`/submissions/${f.id}/review`, "POST", {
                                action: "dismiss",
                                revision: record.revision,
                              });
                              setFeedback(
                                feedback.map((x) =>
                                  x.id === f.id
                                    ? { ...x, status: "dismissed" }
                                    : x,
                                ),
                              );
                            })
                          }
                        >
                          Dismiss
                        </Button>
                      </div>
                    )}
                  </article>
                ))}
              </>
            )}
            {tab === "history" && (
              <>
                <h2>Published versions</h2>
                <p>
                  Restore brings an older version into your draft. It does not
                  change the Guest map.
                </p>
                {versions.map((v) => (
                  <article className="history-card" key={v.version}>
                    <b>Version {v.version}</b>
                    <small>{date(v.publishedAt)}</small>
                    <Button
                      disabled={busy || dirty}
                      onClick={() =>
                        run(async () => {
                          const { map } = await api(
                            `/maps/${mapId}/restore`,
                            "POST",
                            { version: v.version, revision: record.revision },
                          );
                          setRecord({ ...map, role: "admin" });
                          setBase(map.draft);
                          setDoc(map.draft);
                          setMessage(`Version ${v.version} restored to draft.`);
                        })
                      }
                    >
                      Restore to draft
                    </Button>
                  </article>
                ))}
              </>
            )}
            {tab === "settings" && (
              <>
                <h2>Map settings</h2>
                <Button
                  disabled={busy || dirty || !!conflicts.length}
                  onClick={() => run(() => exportBackup(mapId, setMessage))}
                >
                  Download private backup
                </Button>
                <p className="muted">
                  Saved draft, attached photos and private notes. Import from
                  Projects creates a new private map. History, feedback and
                  basemap imagery stay separate.
                </p>
                <Button
                  disabled={busy || dirty}
                  onClick={() =>
                    run(async () => {
                      await api(`/maps/${mapId}/template`, "POST", {
                        name: doc.title,
                        revision: record.revision,
                      });
                      setMessage(
                        "Saved as a reusable template in this project.",
                      );
                    })
                  }
                >
                  Save as my template
                </Button>
                <label>
                  Basemap
                  <select
                    value={doc.basemap}
                    onChange={(e) =>
                      change({ ...doc, basemap: e.target.value as any })
                    }
                  >
                    <option value="aerial">Aerial with labels</option>
                    <option value="streets">Street map</option>
                    <option value="terrain">Terrain</option>
                  </select>
                </label>
                <Button
                  onClick={() => {
                    if (view.current) change({ ...doc, ...view.current });
                    setMessage(
                      "Starting view updated. Save the draft to keep it.",
                    );
                  }}
                >
                  Use current view as starting view
                </Button>
                <h3>Places people can jump to</h3>
                <p className="muted">
                  Save a useful view of an entrance, yard or meeting place.
                  These names appear in the published map.
                </p>
                <Button
                  disabled={busy || doc.savedViews.length >= 100}
                  onClick={() => {
                    if (view.current)
                      change({
                        ...doc,
                        savedViews: [
                          ...doc.savedViews,
                          {
                            id: crypto.randomUUID(),
                            name: `Place ${doc.savedViews.length + 1}`,
                            ...view.current,
                          },
                        ],
                      });
                  }}
                >
                  Save current view as a place
                </Button>
                {doc.savedViews.map((place) => (
                  <div className="saved-place-row" key={place.id}>
                    <input
                      aria-label="Place name"
                      value={place.name}
                      maxLength={160}
                      onChange={(e) =>
                        change({
                          ...doc,
                          savedViews: doc.savedViews.map((v) =>
                            v.id === place.id
                              ? { ...v, name: e.target.value }
                              : v,
                          ),
                        })
                      }
                    />
                    <Button
                      title="Remove saved place"
                      onClick={() =>
                        change({
                          ...doc,
                          savedViews: doc.savedViews.filter(
                            (v) => v.id !== place.id,
                          ),
                        })
                      }
                    >
                      <X size={15} />
                    </Button>
                  </div>
                ))}
                <label>
                  Publication rules
                  <select
                    value={doc.publicationProfile}
                    onChange={(e) =>
                      change({
                        ...doc,
                        publicationProfile: e.target.value as any,
                      })
                    }
                  >
                    <option value="general">General orientation map</option>
                    <option value="51time-driver">
                      Driver reference — no ROW geometry
                    </option>
                  </select>
                </label>
                <label className="check">
                  <input
                    type="checkbox"
                    checked={doc.isExample}
                    onChange={(e) =>
                      change({ ...doc, isExample: e.target.checked })
                    }
                  />
                  Fictional example
                </label>
                <hr />
                <label className="check">
                  <input
                    type="checkbox"
                    checked={record.downloadsAllowed}
                    onChange={(e) =>
                      run(async () => {
                        const downloadsAllowed = e.target.checked;
                        await api(`/maps/${mapId}/settings`, "PATCH", {
                          downloadsAllowed,
                        });
                        setRecord({ ...record, downloadsAllowed });
                      })
                    }
                  />
                  Allow saved maps in the installed app
                </label>
                <p className="muted">
                  Saved maps stay visible with their version and an offline
                  warning. Previously saved copies cannot be erased remotely
                  while a device is offline.
                </p>
                <h3>Last reported copies</h3>
                <p className="muted">
                  This is the last report received. A disconnected device’s
                  current state is unknown.
                </p>
                {syncReports?.reports.map((r: any) => (
                  <div className="sync-row" key={r.deviceId}>
                    <b>{r.name}</b>
                    <small>
                      v{r.version} · Reported {date(r.reportedAt)}
                    </small>
                  </div>
                ))}
              </>
            )}
          </fieldset>
        </aside>
      </div>
      {preview && (
        <Dialog
          title="Review the Guest map"
          close={() => setPreview(false)}
          wide
        >
          <div className="preview-map">
            <MapCanvas document={publishDocument(doc)} mapKey={mapKey} />
          </div>
          <div className="preview-footer">
            <p>
              <b>This is what guests will see.</b>
              <br />
              {
                doc.features.filter(
                  (f) =>
                    f.visibility === "public" && f.reviewState !== "checked",
                ).length
              }{" "}
              visible markings still need your review. Publishing updates the
              existing link.
            </p>
            <Button
              primary
              disabled={busy}
              onClick={() =>
                run(async () => {
                  const result = await api(`/maps/${mapId}/publish`, "POST", {
                    revision: record.revision,
                  });
                  setRecord({
                    ...record,
                    publication: result.version,
                    status: "published",
                  });
                  setPreview(false);
                  setMessage(`Version ${result.version} published.`);
                })
              }
            >
              Publish version {record.publication + 1}
            </Button>
          </div>
        </Dialog>
      )}
      {!!conflicts.length && (
        <Dialog
          title="Keep both devices’ work safe"
          close={() => setMessage("Resolve each conflict before saving.")}
        >
          <p>
            These markings changed in two places. Choose the version to keep for
            each one.
          </p>
          {conflicts.map((c, index) => (
            <article className="conflict" key={c.path}>
              <b>{c.label}</b>
              <div className="row">
                {(["local", "remote"] as const).map((side) => (
                  <Button
                    key={side}
                    onClick={() => {
                      const next = structuredClone(doc);
                      if (c.path.startsWith("features.")) {
                        const id = c.path.slice(9);
                        next.features = next.features.filter(
                          (f) => f.id !== id,
                        );
                        if (c[side]) next.features.push(c[side] as MapFeature);
                      } else (next as any)[c.path] = c[side];
                      setDoc(next);
                      setConflicts((cs) => cs.filter((_, i) => i !== index));
                    }}
                  >
                    Keep {side === "local" ? "this device" : "server version"}
                    {c[side] === undefined ? " (deleted)" : ""}
                  </Button>
                ))}
              </div>
            </article>
          ))}
        </Dialog>
      )}
    </main>
  );
}
function App() {
  const [me, setMe] = useState<{ user: User; workspaces: any[] } | null>(null),
    [config, setConfig] = useState<any>(null),
    [loading, setLoading] = useState(true),
    [error, setError] = useState(""),
    [mapId, setMapId] = useState<string | null>(
      location.pathname.startsWith("/maps/")
        ? location.pathname.split("/")[2]
        : null,
    );
  const guest = location.pathname.startsWith("/v/");
  async function start() {
    setLoading(true);
    setError("");
    try {
      const c = await api("/config");
      setConfig(c);
      if (!guest)
        try {
          setMe(await api("/me"));
        } catch (e) {
          if (!(e instanceof ApiError && e.status === 401)) throw e;
          setMe(null);
        }
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    start();
  }, []);
  if (loading)
    return (
      <div className="loading">
        <Brand />
        <p>Opening your field desk…</p>
      </div>
    );
  if (error)
    return (
      <div className="empty">
        <Notice error>{error}</Notice>
        <Button onClick={start}>Try again</Button>
      </div>
    );
  if (location.pathname === "/reset-password") return <PasswordReset />;
  if (guest)
    return (
      <Guest token={location.pathname.split("/")[2]} mapKey={config.mapKey} />
    );
  if (!me || location.pathname === "/join")
    return <AuthScreen onDone={start} currentUser={me?.user} />;
  return mapId ? (
    <Editor
      mapId={mapId}
      user={me.user}
      mapKey={config.mapKey}
      back={() => {
        history.pushState(null, "", "/");
        setMapId(null);
      }}
    />
  ) : (
    <Dashboard
      {...me}
      mapKey={config.mapKey}
      onMap={(id) => {
        history.pushState(null, "", `/maps/${id}`);
        setMapId(id);
      }}
      onLogout={async () => {
        await api("/auth/sign-out", "POST", {});
        setMe(null);
      }}
    />
  );
}
createRoot(document.getElementById("root")!).render(
  location.pathname.startsWith("/examples") ? <PublicExamples /> : <App />,
);
